// src/components/features/campaigns/CampaignMilestoneTab.jsx
import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Event as EventIcon,
  CheckCircle as CompletedIcon,
  AccessTime as DelayedIcon,
  Cancel as CancelledIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';

// Import services and hooks
import useCampaignStore from '../../stores/campaignStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';
import * as userService from '../../services/userService';

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

// Get status icon and color
const getStatusInfo = (status) => {
  switch (status) {
    case 'completed':
      return { icon: <CompletedIcon />, color: 'success', label: 'Completed' };
    case 'delayed':
      return { icon: <DelayedIcon />, color: 'warning', label: 'Delayed' };
    case 'cancelled':
      return { icon: <CancelledIcon />, color: 'error', label: 'Cancelled' };
    case 'inProgress':
      return { icon: <LinearProgress size={24} />, color: 'info', label: 'In Progress' };
    case 'pending':
    default:
      return { icon: <PendingIcon />, color: 'default', label: 'Pending' };
  }
};

// Status options
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'inProgress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Initial milestone form
const initialMilestoneForm = {
  title: '',
  description: '',
  dueDate: new Date(),
  status: 'pending',
  assignedTo: '',
};

// CampaignMilestoneTab component
const CampaignMilestoneTab = ({ campaign }) => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState({...initialMilestoneForm});
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMilestoneIndex, setSelectedMilestoneIndex] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Hooks
  const { addCampaignMilestone, updateCampaignMilestone } = useCampaignStore();
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Get campaign milestones
  const milestones = campaign?.milestones || [];
  
  // Check if user can manage milestones
  const canManageMilestones = hasPermission('campaigns', 'update');
  
  // Open milestone dialog
  const handleAddMilestone = async () => {
    setMilestoneForm({...initialMilestoneForm});
    setIsEditing(false);
    setOpenDialog(true);
    
    // Load users for assignment
    try {
      setLoadingUsers(true);
      const response = await userService.getUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.log('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };
  
  // Close milestone dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMilestoneForm({...initialMilestoneForm});
    setIsEditing(false);
  };
  
  // Handle form field changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setMilestoneForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle date change
  const handleDateChange = (newDate) => {
    setMilestoneForm(prev => ({
      ...prev,
      dueDate: newDate
    }));
  };
  
  // Submit milestone
  const handleSubmitMilestone = async () => {
    // Validate form
    if (!milestoneForm.title || !milestoneForm.dueDate) {
      showError('Please fill in all required fields');
      return;
    }
    
    try {
      if (isEditing && selectedMilestoneIndex !== null) {
        // Get the milestone ID
        const milestoneId = milestones[selectedMilestoneIndex]._id;
        await updateCampaignMilestone(campaign._id, milestoneId, milestoneForm);
        showSuccess('Milestone updated successfully');
      } else {
        await addCampaignMilestone(campaign._id, milestoneForm);
        showSuccess('Milestone added successfully');
      }
      handleCloseDialog();
    } catch (error) {
      showError(error.message || 'Failed to save milestone');
    }
  };
  
  // Handle milestone menu
  const handleOpenMenu = (event, index) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMilestoneIndex(index);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedMilestoneIndex(null);
  };
  
  // Handle edit milestone
  const handleEditMilestone = async () => {
    handleCloseMenu();
    if (selectedMilestoneIndex === null) return;
    
    // Load users for assignment
    try {
      setLoadingUsers(true);
      const response = await userService.getUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.log('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
    
    const milestone = milestones[selectedMilestoneIndex];
    setMilestoneForm({
      title: milestone.title,
      description: milestone.description || '',
      dueDate: new Date(milestone.dueDate),
      status: milestone.status,
      assignedTo: milestone.assignedTo?._id || milestone.assignedTo || '',
    });
    setIsEditing(true);
    setOpenDialog(true);
  };
  
  // Calculate progress percentage
  const calculateProgress = () => {
    if (!milestones.length) return 0;
    
    const completedCount = milestones.filter(
      m => m.status === 'completed'
    ).length;
    
    return Math.round((completedCount / milestones.length) * 100);
  };
  
  // Get milestone progress color
  const getProgressColor = (progress) => {
    if (progress >= 100) return 'success';
    if (progress >= 70) return 'info';
    if (progress >= 30) return 'warning';
    return 'error';
  };
  
  // Progress percentage
  const progressPercentage = calculateProgress();
  
  // Sort milestones by due date
  const sortedMilestones = [...milestones].sort((a, b) => {
    return new Date(a.dueDate) - new Date(b.dueDate);
  });
  
  // Group milestones by status
  const milestoneGroups = {
    pending: sortedMilestones.filter(m => m.status === 'pending' || m.status === 'inProgress'),
    completed: sortedMilestones.filter(m => m.status === 'completed'),
    other: sortedMilestones.filter(m => m.status === 'delayed' || m.status === 'cancelled'),
  };
  
  return (
    <Grid container spacing={3}>
      {/* Milestones Overview Card */}
      <Grid item xs={12}>
        <Card elevation={0} variant="outlined">
          <CardHeader 
            title="Campaign Milestones" 
            avatar={<FlagIcon  />}
            action={
              canManageMilestones && (
                <Button
                  variant="contained"
                  
                  startIcon={<AddIcon />}
                  onClick={handleAddMilestone}
                >
                  Add Milestone
                </Button>
              )
            }
          />
          <Divider />
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Milestone Progress: {progressPercentage}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={progressPercentage} 
                color={getProgressColor(progressPercentage)}
                sx={{ height: 10, borderRadius: 1 }}
              />
            </Box>
            
            {milestones.length === 0 ? (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
                border: `1px dashed ${theme.palette.divider}`
              }}>
                <Typography color="textSecondary" gutterBottom>
                  No milestones defined yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Create milestones to track key phases and deliverables of your campaign
                </Typography>
                {canManageMilestones && (
                  <Button
                    variant="contained"
                    
                    startIcon={<AddIcon />}
                    onClick={handleAddMilestone}
                    sx={{ mt: 2 }}
                  >
                    Add First Milestone
                  </Button>
                )}
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Pending & In Progress Milestones */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Upcoming & In Progress
                  </Typography>
                  <List>
                    {milestoneGroups.pending.length > 0 ? (
                      milestoneGroups.pending.map((milestone, index) => {
                        const statusInfo = getStatusInfo(milestone.status);
                        const isPastDue = new Date(milestone.dueDate) < new Date() && milestone.status !== 'completed';
                        
                        return (
                          <Box key={index} sx={{ mb: 2 }}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                borderColor: isPastDue ? theme.palette.error.main : undefined,
                                bgcolor: isPastDue ? alpha(theme.palette.error.main, 0.05) : undefined
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar
                                    sx={{ 
                                      bgcolor: theme.palette[statusInfo.color]?.main || theme.palette.grey[400],
                                      mr: 2,
                                      width: 32,
                                      height: 32
                                    }}
                                  >
                                    {statusInfo.icon}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {milestone.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                      <Chip 
                                        label={statusInfo.label} 
                                        size="small" 
                                        color={statusInfo.color}
                                      />
                                      {isPastDue && (
                                        <Chip 
                                          label="Past Due" 
                                          size="small" 
                                          color="error"
                                        />
                                      )}
                                      <Chip
                                        icon={<EventIcon fontSize="small" />}
                                        label={`Due: ${formatDate(milestone.dueDate)}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                                {canManageMilestones && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleOpenMenu(e, index)}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              
                              {milestone.description && (
                                <Typography 
                                  variant="body2" 
                                  color="textSecondary" 
                                  sx={{ mt: 1.5, ml: 6 }}
                                >
                                  {milestone.description}
                                </Typography>
                              )}
                              
                              {milestone.assignedTo && (
                                <Box sx={{ mt: 1.5, ml: 6, display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                                    Assigned to:
                                  </Typography>
                                  <Chip
                                    icon={<PersonIcon fontSize="small" />}
                                    label={milestone.assignedTo.firstName ? 
                                      `${milestone.assignedTo.firstName} ${milestone.assignedTo.lastName}` : 
                                      'Unnamed User'
                                    }
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              )}
                            </Paper>
                          </Box>
                        );
                      })
                    ) : (
                      <Typography color="textSecondary">
                        No upcoming milestones
                      </Typography>
                    )}
                  </List>
                </Grid>
                
                {/* Completed Milestones */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Completed
                  </Typography>
                  <List>
                    {milestoneGroups.completed.length > 0 ? (
                      milestoneGroups.completed.map((milestone, index) => {
                        const actualIndex = milestones.findIndex(m => m._id === milestone._id);
                        const statusInfo = getStatusInfo(milestone.status);
                        
                        return (
                          <Box key={index} sx={{ mb: 2 }}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                bgcolor: alpha(theme.palette.success.main, 0.05)
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar
                                    sx={{ 
                                      bgcolor: theme.palette.success.main,
                                      mr: 2,
                                      width: 32,
                                      height: 32
                                    }}
                                  >
                                    <CompletedIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {milestone.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                      <Chip 
                                        label="Completed" 
                                        size="small" 
                                        color="success"
                                      />
                                      {milestone.completedDate && (
                                        <Chip
                                          icon={<EventIcon fontSize="small" />}
                                          label={`Completed: ${formatDate(milestone.completedDate)}`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                                {canManageMilestones && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleOpenMenu(e, actualIndex)}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              
                              {milestone.description && (
                                <Typography 
                                  variant="body2" 
                                  color="textSecondary" 
                                  sx={{ mt: 1.5, ml: 6 }}
                                >
                                  {milestone.description}
                                </Typography>
                              )}
                            </Paper>
                          </Box>
                        );
                      })
                    ) : (
                      <Typography color="textSecondary">
                        No completed milestones
                      </Typography>
                    )}
                  </List>
                </Grid>
                
                {/* Delayed & Cancelled Milestones */}
                {milestoneGroups.other.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Delayed & Cancelled
                    </Typography>
                    <List>
                      {milestoneGroups.other.map((milestone, index) => {
                        const actualIndex = milestones.findIndex(m => m._id === milestone._id);
                        const statusInfo = getStatusInfo(milestone.status);
                        
                        return (
                          <Box key={index} sx={{ mb: 2 }}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                bgcolor: milestone.status === 'delayed' ? 
                                  alpha(theme.palette.warning.main, 0.05) : 
                                  alpha(theme.palette.error.main, 0.05)
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar
                                    sx={{ 
                                      bgcolor: theme.palette[statusInfo.color].main,
                                      mr: 2,
                                      width: 32,
                                      height: 32
                                    }}
                                  >
                                    {statusInfo.icon}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {milestone.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                      <Chip 
                                        label={statusInfo.label} 
                                        size="small" 
                                        color={statusInfo.color}
                                      />
                                      <Chip
                                        icon={<EventIcon fontSize="small" />}
                                        label={`Due: ${formatDate(milestone.dueDate)}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                                {canManageMilestones && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleOpenMenu(e, actualIndex)}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              
                              {milestone.description && (
                                <Typography 
                                  variant="body2" 
                                  color="textSecondary" 
                                  sx={{ mt: 1.5, ml: 6 }}
                                >
                                  {milestone.description}
                                </Typography>
                              )}
                            </Paper>
                          </Box>
                        );
                      })}
                    </List>
                  </Grid>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      {/* Milestone Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleEditMilestone}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit Milestone
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete</Typography>
        </MenuItem>
      </Menu>
      
      {/* Milestone Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? "Edit Milestone" : "Add Milestone"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Milestone Title"
                name="title"
                value={milestoneForm.title}
                onChange={handleFormChange}
                fullWidth
                required
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Due Date"
                value={milestoneForm.dueDate}
                onChange={handleDateChange}
                renderInput={(params) => 
                  <TextField 
                    {...params} 
                    fullWidth 
                    margin="normal"
                    required
                  />
                }
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={milestoneForm.status}
                  onChange={handleFormChange}
                  label="Status"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="assignedTo-label">Assigned To</InputLabel>
                <Select
                  labelId="assignedTo-label"
                  id="assignedTo"
                  name="assignedTo"
                  value={milestoneForm.assignedTo}
                  onChange={handleFormChange}
                  label="Assigned To"
                  disabled={loadingUsers}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={milestoneForm.description}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={3}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitMilestone} 
            variant="contained" 
            
            disabled={!milestoneForm.title || !milestoneForm.dueDate}
          >
            {isEditing ? "Update Milestone" : "Add Milestone"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default CampaignMilestoneTab;